import { useState, type ReactNode, useEffect } from 'react';
import { useLocation } from 'react-router-dom';
import { ToggleSwitch, useCurrentUser } from '@beholos/holoscommon';
import Slider from 'rc-slider';
import 'rc-slider/assets/index.css';
import { ChevronDown } from 'lucide-react';
import type { ScheduleFilters } from '../../services/schedule.service';
import { fetchPartners, fetchCategories, fetchLocations } from '../../services/schedule.service';
import { parseISO, format, isValid } from 'date-fns';
import { es } from 'date-fns/locale';

interface FiltersProps {
  onFilterChange: (filters: Partial<ScheduleFilters>) => void;
  filters: ScheduleFilters;
  showNutritionist?: boolean;
  showHealthCoach?: boolean;
  showFinancialAdvisor?: boolean;
  showPsychologist?: boolean;
  showPersonalTrainer?: boolean;
}

interface Partner {
  _id: string;
  name: string;
}

interface Category {
  _id: string;
  id?: string;
  name: string;
  image?: string;
  slug?: string;
}

interface Location {
  countries: Array<{
    id: string;
    name: string;
    image: string;
  }>;
  cities: Array<{
    id: string;
    name: string;
  }>;
  districts: Array<{
    id: string;
    _id?: string;
    name: string;
  }>;
}

const ToggleButton = (
  {
    value,
    label,
    selected,
    onChange
  }: {
    value: string;
    label: string;
    selected: boolean;
    onChange: (value: string) => void;
  }
) => (
  <div
    onClick={() => onChange(value)}
    className={`text-[10px] font-bold mr-2 px-2 py-1 cursor-pointer rounded ${selected ? 'bg-[#343a40] text-white' : 'bg-[#f8f9fa]'
      }`}
  >
    {label}
  </div>
);

const Accordion = ({ title, children, count }: { title: string; children: ReactNode; count: number }) => {
  const [open, setOpen] = useState(false);
  return (
    <div className="border-t border-gray-200">
      <button
        onClick={() => setOpen(!open)}
        className="w-full flex justify-between items-center py-3 cursor-pointer"
      >
        <span className="font-semibold">{title}</span>
        <div className="flex items-center">
          <span className="text-sm text-gray-500 mr-2">{count}</span>
          <ChevronDown className={`transition-transform ${open ? 'rotate-180' : ''}`} />
        </div>
      </button>
      {open && <div className="pb-3 px-1 space-y-2">{children}</div>}
    </div>
  );
};

const CheckboxList = ({
  options,
  selected,
  onChange
}: {
  options: { value: string; label: string }[];
  selected: string[];
  onChange: (value: string) => void;
}) => {
  const [filter, setFilter] = useState('');
  return (
    <div>
      <input
        type="text"
        placeholder="Filtrar..."
        className="mb-2 w-full px-2 py-1 border rounded text-sm"
        value={filter}
        onChange={(e) => setFilter(e.target.value)}
      />
      <div className="space-y-1">
        {options
          .filter((opt) => opt.label.toLowerCase().includes(filter.toLowerCase()))
          .map((opt) => (
            <label key={opt.value} className="flex items-center text-sm space-x-2">
              <input
                type="checkbox"
                checked={selected.includes(opt.value)}
                onChange={() => {

                  onChange(opt.value);
                }}
              />
              <span>{opt.label}</span>
            </label>
          ))}
      </div>
    </div>
  );
};

export default function Filters({
  onFilterChange,
  filters,
  showNutritionist = false,
  showHealthCoach = false,
  showFinancialAdvisor = false,
  showPsychologist = false,
  showPersonalTrainer = false
}: FiltersProps) {
  const [selectedModality, setSelectedModality] = useState<string[]>([]);
  const [selectedCountries, setSelectedCountries] = useState<string[]>([]);
  const [creditsValue, setCreditsValue] = useState<number>(8);
  const [hourValue, setHourValue] = useState<number>(4);
  const [partners, setPartners] = useState<Partner[]>([]);
  const [selectedPartners, setSelectedPartners] = useState<string[]>([]);
  const [loadingPartners, setLoadingPartners] = useState(false);
  const [categories, setCategories] = useState<Category[]>([]);
  const [selectedCategories, setSelectedCategories] = useState<string[]>([]);
  const [loadingCategories, setLoadingCategories] = useState(false);
  const [locations, setLocations] = useState<Location | null>(null);
  const [selectedCities, setSelectedCities] = useState<string[]>([]);
  const [selectedDistricts, setSelectedDistricts] = useState<string[]>([]);
  const [loadingLocations, setLoadingLocations] = useState(false);
  const [saveFilters, setSaveFilters] = useState<boolean>(false);
  const [filtersLoaded, setFiltersLoaded] = useState<boolean>(false);
  const location = useLocation();
  const categoryParam = new URLSearchParams(location.search).get('category');
  const [pendingCategoryName, setPendingCategoryName] = useState<string | null>(categoryParam);
  const { user } = useCurrentUser();
  const [appliedUserCountry, setAppliedUserCountry] = useState<boolean>(false);
  const [inPlan, setInPlan] = useState<boolean>(false);

  // Check if any filters are active
  const hasActiveFilters = () => {
    return selectedModality.length > 0 ||
      selectedCountries.length > 0 ||
      selectedPartners.length > 0 ||
      selectedCategories.length > 0 ||
      selectedCities.length > 0 ||
      selectedDistricts.length > 0 ||
      creditsValue !== 8 ||
      hourValue !== 4;
  };

  // Load saved filters from localStorage on mount
  useEffect(() => {
    const savedFilters = localStorage.getItem('holos-class-filters');
    const savedSavePreference = localStorage.getItem('holos-save-filters-preference');

    if (savedSavePreference === 'true') {
      setSaveFilters(true);

      if (savedFilters) {
        try {
          const parsed = JSON.parse(savedFilters);
          // Apply saved filters to state
          if (parsed.selectedModality) setSelectedModality(parsed.selectedModality);
          if (parsed.selectedCountries) setSelectedCountries(parsed.selectedCountries);
          if (parsed.creditsValue) setCreditsValue(parsed.creditsValue);
          if (parsed.hourValue) setHourValue(parsed.hourValue);
          if (parsed.selectedPartners) setSelectedPartners(parsed.selectedPartners);
          if (parsed.selectedCategories) setSelectedCategories(parsed.selectedCategories);
          if (parsed.selectedCities) setSelectedCities(parsed.selectedCities);
          if (parsed.selectedDistricts) setSelectedDistricts(parsed.selectedDistricts);
        } catch (error) {
          console.error('Error parsing saved filters:', error);
        }
      }
    }
    setFiltersLoaded(true);
  }, []);

  // Apply loaded filters to the API - only once after loading
  useEffect(() => {
    if (filtersLoaded && saveFilters) {
      // Apply all current filter states to the API
      const filtersToApply: Partial<ScheduleFilters> = {};

      if (selectedModality.length > 0) {
        if (selectedModality.includes('online')) {
          filtersToApply.liveStreamClass = true;
        } else if (selectedModality.includes('presencial')) {
          filtersToApply.liveStreamClass = false;
        }
      }

      if (selectedCategories.length > 0) {
        filtersToApply.categories = selectedCategories;
      }

      if (selectedPartners.length > 0) {
        filtersToApply.studios = selectedPartners;
      }

      if (creditsValue !== 8) { // Only apply if different from default
        filtersToApply.passes = creditsValue;
      }

      if (hourValue !== 4) { // Only apply if different from default
        const hourString = hourValue.toString().padStart(2, '0') + ':00';
        filtersToApply.start = hourString;
      }

      if (selectedCities.length > 0) {
        filtersToApply.cities = selectedCities;
      }

      if (selectedDistricts.length > 0) {
        filtersToApply.districts = selectedDistricts;
      }

      if (selectedCountries.length > 0) {
        filtersToApply.countries = selectedCountries;
      }

      // Only apply if there are actual filters to apply
      if (Object.keys(filtersToApply).length > 0) {

        onFilterChange(filtersToApply);
      }
    }
  }, [filtersLoaded]); // Only run once when filters are loaded

  // Save filters to localStorage when save is enabled and filters change
  useEffect(() => {
    if (saveFilters) {
      const filtersToSave = {
        selectedModality,
        selectedCountries,
        creditsValue,
        hourValue,
        selectedPartners,
        selectedCategories,
        selectedCities,
        selectedDistricts
      };
      localStorage.setItem('holos-class-filters', JSON.stringify(filtersToSave));
    }
  }, [saveFilters, selectedModality, selectedCountries, creditsValue, hourValue, selectedPartners, selectedCategories, selectedCities, selectedDistricts]);

  // Handle save filters preference
  const handleSaveFiltersChange = (checked: boolean) => {
    setSaveFilters(checked);
    localStorage.setItem('holos-save-filters-preference', checked.toString());

    if (!checked) {
      localStorage.removeItem('holos-class-filters');
    }
  };

  // Fetch partners on component mount
  useEffect(() => {
    loadPartners();
  }, []);

  // Fetch categories on component mount
  useEffect(() => {
    const loadCategories = async () => {
      try {
        setLoadingCategories(true);
        const categoriesData = await fetchCategories();
        setCategories(categoriesData);
      } catch (error) {
        console.error('Error loading categories:', error);
      } finally {
        setLoadingCategories(false);
      }
    };

    loadCategories();
  }, []);

  // Apply category from query param once categories are loaded
  useEffect(() => {
    if (pendingCategoryName && categories.length > 0) {
      const lower = pendingCategoryName.toLowerCase();
      const match = categories.find(cat => (cat.name || '').toLowerCase() === lower);
      if (match && match.id) {
        setSelectedCategories([match.id as string]);
        onFilterChange({ categories: [match.id as string] });
      }
      setPendingCategoryName(null);
    }
  }, [pendingCategoryName, categories]);

  // Fetch locations on component mount
  useEffect(() => {
    const loadLocations = async () => {
      try {
        setLoadingLocations(true);
        const locationsData = await fetchLocations();
        setLocations(locationsData);
      } catch (error) {
        console.error('Error loading locations:', error);
      } finally {
        setLoadingLocations(false);
      }
    };

    loadLocations();
  }, []);

  // Preselect user's country once locations are loaded
  useEffect(() => {
    if (appliedUserCountry) return;
    if (!locations || loadingLocations) return;
    if (!user || !(user as any).address) return;
    if (selectedCountries.length > 0) return;

    const address = (user as any).address as { countryCode?: string; _id?: string };
    const countries = locations.countries || [];
    if (countries.length === 0) return;

    const normalize = (s: string) => s.toLowerCase().normalize('NFD').replace(/\p{Diacritic}+/gu, '');

    let matchId: string | undefined;
    if (address._id) {
      const byId = countries.find(c => String(c.id) === String(address._id));
      if (byId) matchId = byId.id;
    }
    if (!matchId && address.countryCode) {
      const target = normalize(String(address.countryCode));
      const byName = countries.find(c => normalize(String(c.name)) === target);
      if (byName) matchId = byName.id;
    }

    if (matchId) {
      setSelectedCountries([matchId]);
      onFilterChange({ countries: [matchId] });
      setAppliedUserCountry(true);
    }
  }, [user, locations, loadingLocations, selectedCountries, appliedUserCountry, onFilterChange]);

  // Initialize modality based on current filters
  useEffect(() => {
    if (filters.liveStreamClass === true) {
      setSelectedModality(['online']);
    } else if (filters.liveStreamClass === false) {
      setSelectedModality(['presencial']);
    } else {
      setSelectedModality([]);
    }
  }, [filters.liveStreamClass]);

  // Initialize credits value based on current filters
  useEffect(() => {
    if (filters.passes) {
      setCreditsValue(filters.passes);
    }
  }, [filters.passes]);

  // Initialize hour value based on current filters
  useEffect(() => {
    if (filters.start) {
      // Convert hour string to number (e.g., "04:00" -> 4)
      const hour = parseInt(filters.start.split(':')[0]);
      setHourValue(hour);
    }
  }, [filters.start]);

  // Initialize selected filters from current filters
  useEffect(() => {
    if (filters.studios) {
      const studiosArray = Array.isArray(filters.studios) ? filters.studios : [filters.studios];
      setSelectedPartners(studiosArray);
    }
  }, [filters.studios]);

  useEffect(() => {
    if (filters.categories) {
      const categoriesArray = Array.isArray(filters.categories) ? filters.categories : [filters.categories];
      setSelectedCategories(categoriesArray);
    }
  }, [filters.categories]);

  useEffect(() => {
    if (filters.cities) {
      const citiesArray = Array.isArray(filters.cities) ? filters.cities : [filters.cities];
      setSelectedCities(citiesArray);
    }
  }, [filters.cities]);

  useEffect(() => {
    if (filters.districts) {
      const districtsArray = Array.isArray(filters.districts) ? filters.districts : [filters.districts];
      setSelectedDistricts(districtsArray);
    }
  }, [filters.districts]);

  useEffect(() => {
    if (filters.countries) {
      const countriesArray = Array.isArray(filters.countries) ? filters.countries : [filters.countries];
      setSelectedCountries(countriesArray);
    }
  }, [filters.countries]);

  useEffect(() => {
    setSelectedPartners([]);
    loadPartners();
    onFilterChange({inPlan });
  }, [inPlan]);

  const loadPartners = async () => {
    try {
      setLoadingPartners(true);
      const partnersData = await fetchPartners({
        showNutritionist,
        showHealthCoach,
        showFinancialAdvisor,
        showPsychologist,
        showPersonalTrainer,
        inPlan: inPlan
      });
      setPartners(partnersData);
    } catch (error) {
      console.error('Error loading partners:', error);
    } finally {
      setLoadingPartners(false);
    }
  };

  const handleModalityChange = (value: string) => {
    // Toggle behavior: clicking the selected option again unselects it
    if (selectedModality.includes(value)) {
      setSelectedModality([]);
      onFilterChange({ liveStreamClass: undefined });
      return;
    }

    // Set the clicked modality as the only selected one
    const newModality = [value];
    setSelectedModality(newModality);

    // Map modality to API filters
    const isOnline = newModality.includes('online');
    const isPresencial = newModality.includes('presencial');

    if (isOnline) {
      // When selecting online, clear location filters so we don't restrict by districts/cities
      setSelectedCities([]);
      setSelectedDistricts([]);
      onFilterChange({ liveStreamClass: true, cities: undefined, districts: undefined });
    } else if (isPresencial) {
      onFilterChange({ liveStreamClass: false });
    } else {
      onFilterChange({ liveStreamClass: undefined });
    }
  };

  const handleHourChange = (value: number | number[]) => {
    const numericValue = Array.isArray(value) ? value[0] : value;
    setHourValue(numericValue);
    // Adjust by -1h for API filtering alignment
    const apiHour = Math.max(0, numericValue);
    const hourString = apiHour.toString().padStart(2, '0') + ':00';
    onFilterChange({ start: hourString });
  };

  const handlePartnerChange = (partnerId: string) => {
    const newSelectedPartners = selectedPartners.includes(partnerId)
      ? selectedPartners.filter(id => id !== partnerId)
      : [...selectedPartners, partnerId];

    setSelectedPartners(newSelectedPartners);
    onFilterChange({ studios: newSelectedPartners });
  };

  const handleCategoryChange = (categoryId: string) => {
    const newSelectedCategories = selectedCategories.includes(categoryId)
      ? selectedCategories.filter(id => id !== categoryId)
      : [...selectedCategories, categoryId];

    setSelectedCategories(newSelectedCategories);
    onFilterChange({ categories: newSelectedCategories });
  };

  const handleCityChange = (cityId: string) => {

    const newSelectedCities = selectedCities.includes(cityId)
      ? selectedCities.filter(id => id !== cityId)
      : [...selectedCities, cityId];


    setSelectedCities(newSelectedCities);
    onFilterChange({ cities: newSelectedCities.length > 0 ? newSelectedCities : undefined });
  };

  const handleDistrictChange = (districtId: string) => {

    const newSelectedDistricts = selectedDistricts.includes(districtId)
      ? selectedDistricts.filter(id => id !== districtId)
      : [...selectedDistricts, districtId];


    setSelectedDistricts(newSelectedDistricts);
    onFilterChange({ districts: newSelectedDistricts.length > 0 ? newSelectedDistricts : undefined });
  };

  const handleCountryChange = (countryId: string) => {

    const newSelectedCountries = selectedCountries.includes(countryId)
      ? selectedCountries.filter(id => id !== countryId)
      : [...selectedCountries, countryId];


    setSelectedCountries(newSelectedCountries);
    onFilterChange({ countries: newSelectedCountries.length > 0 ? newSelectedCountries : undefined });
  };

  const handleResetFilters = () => {
    setSelectedModality([]);
    setSelectedCountries([]);
    setCreditsValue(8);
    setHourValue(4);
    setSelectedPartners([]);
    setSelectedCategories([]);
    setSelectedCities([]);
    setSelectedDistricts([]);

    // Clear localStorage if save is enabled
    if (saveFilters) {
      localStorage.removeItem('holos-class-filters');
    }

    onFilterChange({
      liveStreamClass: undefined,
      categories: undefined,
      studios: undefined,
      passes: undefined,
      start: undefined,
      showOnlyOneOnOne: undefined,
      cities: undefined,
      districts: undefined,
      countries: undefined
    });
  };

  // Convert hour number to display format (e.g., 4 -> "04:00")
  const formatHour = (hour: number) => {
    return hour.toString().padStart(2, '0') + ':00';
  };

  // Get current date in Spanish format
  const getCurrentDateText = () => {
    let dateToUse: Date;

    if (filters.date) {
      // Use date-fns parseISO for better timezone handling
      const parsedDate = parseISO(filters.date);

      // Check if the parsed date is valid
      if (isValid(parsedDate)) {
        dateToUse = parsedDate;
      } else {
        // Fallback to manual parsing if parseISO fails
        const [year, month, day] = filters.date.split('-').map(Number);
        dateToUse = new Date(year, month - 1, day);
      }
    } else {
      dateToUse = new Date();
    }

    // Use date-fns format with Spanish locale for consistent formatting
    const dayName = format(dateToUse, 'EEEE', { locale: es });
    const formattedDate = format(dateToUse, 'dd/MM', { locale: es });

    return `${dayName} ${formattedDate}`;
  };

  return (
    <div className="min-h-[calc(100vh-80px)] max-h-[calc(100vh-80px)] text-sm bg-sand">
      <div className="max-w-[100%] mx-auto pt-10">
        <div className="flex mb-6 items-center justify-between">
          <div className="font-bold mr-2 flex-grow whitespace-nowrap text-[14px] text-[#4f4f4f]">
            Filtra tu búsqueda
          </div>
          {hasActiveFilters() && (
            <div
              className="bg-red py-1 px-1 flex justify-center items-center cursor-pointer"
              style={{ borderRadius: '5px' }}
              onClick={handleResetFilters}
            >
              <div className="text-white font-medium uppercase text-[8px] whitespace-nowrap">
                restablecer filtros
              </div>
            </div>
          )}
        </div>
        <div className="flex mb-2 border-b border-gray-300 pb-2 w-full">
          <label
            className="whitespace-nowrap flex items-center flex-grow"
            htmlFor="saveFilters"
          >
            <input
              type="checkbox"
              id="saveFilters"
              className="inline-block h-5 leading-5 p-0 m-0 align-bottom relative top-[1px]"
              checked={saveFilters}
              onChange={(e) => handleSaveFiltersChange(e.target.checked)}
            />
            <span className="ml-1 whitespace-nowrap font-bold">Guarda tus filtros</span>
          </label>
        </div>
      </div>

      <div className="mb-2">
        <div className="border-b border-gray-300 ">
          <div className="py-4">
            <div className="flex">
              <label
                className="flex-grow text-base font-extrabold  text-[#4f4f4f] cursor-default"
              >
                Modalidad de la clase
              </label>
            </div>
            <div className="flex my-2">
              <ToggleButton
                label="PRESENCIAL"
                value="presencial"
                selected={selectedModality.includes('presencial')}
                onChange={handleModalityChange}
              />
              <ToggleButton
                label="ONLINE"
                value="online"
                selected={selectedModality.includes('online')}
                onChange={handleModalityChange}
              />
            </div>
          </div>
        </div>
      </div>

      {/* Hour Slider */}
      <div>
        <div className="py-4">
          <label className="text-base font-extrabold text-[#4f4f4f] cursor-default">
            Horario
          </label>
          <div>
            <div>
              <div>
                <div className="font-bold mt-2 mb-2 uppercase text-[9px]">
                  <span className="text-holos uppercase">{getCurrentDateText()}</span>
                  {`, después de las ${formatHour(hourValue)}.`}
                </div>
                <div className="mx-2">
                  <Slider
                    min={0}
                    max={23}
                    value={hourValue}
                    onChange={handleHourChange}
                    className="my-5 h-5"
                    trackStyle={{ backgroundColor: '#A3BDF5', height: 5 }}
                    handleStyle={{
                      borderColor: '#A3BDF5',
                      height: 20,
                      width: 20,
                      marginTop: -8,
                      backgroundColor: '#A3BDF5',
                    }}
                    railStyle={{ backgroundColor: '#e5e7eb', height: 5 }} // gray-300
                  />
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        className="cursor-pointer flex flex-row items-center justify-between border-t border-gray-300 py-4"
        onClick={() => setInPlan((prev) => !prev)}>
        <div className="flex-grow">
          <div className="text-base font-extrabold text-[#4f4f4f]">Experiencias Disponibles</div>
          <div className="text-sm text-gray-500">Incluido en tu membresía</div>
        </div>
        <div onClick={(e) => e.stopPropagation()}>
          <ToggleSwitch
            label=""
            on={inPlan}
            setOn={setInPlan}
          />
        </div>
      </div>

      {/* Accordions */}
      <Accordion title="Partners" count={selectedPartners.length}>
        {loadingPartners ? (
          <div className="text-sm text-gray-500">Cargando partners...</div>
        ) : (
          <CheckboxList
            key="partners"
            options={partners.map(partner => ({
              value: partner._id,
              label: partner.name
            }))}
            selected={selectedPartners}
            onChange={handlePartnerChange}
          />
        )}
      </Accordion>
      <Accordion title="Actividades" count={selectedCategories.length}>
        {loadingCategories ? (
          <div className="text-sm text-gray-500">Cargando actividades...</div>
        ) : (
          <CheckboxList
            key="categories"
            options={categories
              .filter(category => !!category.id)
              .map(category => ({
                value: category.id as string,
                label: category.name
              }))}
            selected={selectedCategories}
            onChange={handleCategoryChange}
          />
        )}
      </Accordion>
      <Accordion title="Distritos" count={selectedDistricts.length}>
        {loadingLocations ? (
          <div className="text-sm text-gray-500">Cargando distritos...</div>
        ) : (
          <CheckboxList
            key="districts"
            options={(locations?.districts || []).map(district => {
              // Use _id if available, otherwise fall back to id
              const districtId = district._id || district.id;
              return {
                value: districtId,
                label: district.name
              };
            })}
            selected={selectedDistricts}
            onChange={handleDistrictChange}
          />
        )}
      </Accordion>
      <Accordion title="Ciudades" count={selectedCities.length}>
        {loadingLocations ? (
          <div className="text-sm text-gray-500">Cargando ciudades...</div>
        ) : (
          <CheckboxList
            key="cities"
            options={(locations?.cities || []).map(city => ({
              value: city.id,
              label: city.name
            }))}
            selected={selectedCities}
            onChange={handleCityChange}
          />
        )}
      </Accordion>
      <Accordion title="Países" count={selectedCountries.length}>
        {loadingLocations ? (
          <div className="text-sm text-gray-500">Cargando países...</div>
        ) : (
          <CheckboxList
            key="countries"
            options={(locations?.countries || []).map(country => ({
              value: country.id,
              label: country.name
            }))}
            selected={selectedCountries}
            onChange={handleCountryChange}
          />
        )}
      </Accordion>
    </div>
  );
}
